import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { LinkRequest } from './links.model';
import { LinksService } from './links.service';

@Component({
  selector: 'app-links',
  standalone: false,
  templateUrl: './links.component.html',
  styleUrl: './links.component.css'
})
export class LinksComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  links: any[] = [];
  filteredLinks: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  linkId = 0;
  urlKh = '';
  urlEn = '';
  sortOrder = 0;
  isEnabled = true;
  image: File | null = null;
  imagePreview = '';

  titleKh = '';
  titleEn = '';
  searchText = '';

  constructor(
    private linksService: LinksService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadLinks('');
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);

      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }
    });
  }

  loadLinks(langCode: string) {
    this.linksService.getAllLinks(langCode).subscribe((res: any) => {
      const apiData: any[] = res.data || [];

      this.links = apiData
        .filter(item => item.translations && item.translations.length > 0)
        .flatMap(item =>
          item.translations.map((t: any) => ({
            linkId: item.linkId,
            url: t.url ?? item.url ?? '',
            icon: item.icon,
            sortOrder: item.sortOrder,
            isEnabled: item.isEnabled,
            languageCode: t.languageCode,
            title: t.title
          }))
        );

      this.filterLinks(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;

    this.filterLinks(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterLinks(tab: string) {
    const keyword = this.searchText.trim().toLowerCase();
    this.filteredLinks = this.links.filter(r => {
      const matchesLanguage = r.languageCode === tab;
      const matchesSearch = !keyword ||
        `${r.title ?? ''} ${r.url ?? ''} ${r.sortOrder ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesLanguage && matchesSearch;
    });
  }

  onSearchChange() {
    this.currentPage = 1;
    this.filterLinks(this.activeTab || 'kh');
  }

  getLanguageCount(lang: string): number {
    return this.links.filter(r => r.languageCode === lang).length;
  }

  get pagedLinks(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredLinks.slice(start, end);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  totalPages(): number {
    return Math.ceil(this.filteredLinks.length / this.pageSize);
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.linkId = id;

    this.linksService.getLinkById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;

          this.linkId = data.linkId;
          this.sortOrder = data.sortOrder ?? 0;
          this.isEnabled = data.isEnabled;
          this.image = null;
          this.imagePreview = data.icon || '';
          this.titleKh = '';
          this.titleEn = '';
          this.urlKh = '';
          this.urlEn = '';

          data.translations.forEach((item: any) => {
            if (item.languageCode === 'kh') {
              this.titleKh = item.title;
              this.urlKh = item.url ?? data.url ?? '';
            } else if (item.languageCode === 'en') {
              this.titleEn = item.title;
              this.urlEn = item.url ?? data.url ?? '';
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: () => {
        this.errorMessage = 'Failed to load link data';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  onFileSelected(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.image = file;
      this.imagePreview = URL.createObjectURL(file);
    }
  }

  getRequest(): LinkRequest {
    return {
      image: this.image instanceof File ? this.image : undefined,
      data: {
        sortOrder: this.sortOrder,
        isEnabled: this.isEnabled,
        translations: [
          {
            languageId: 1,
            title: this.titleKh ?? '',
            url: this.urlKh ?? ''
          },
          {
            languageId: 2,
            title: this.titleEn ?? '',
            url: this.urlEn ?? ''
          }
        ]
      }
    };
  }

  resetForm() {
    this.linkId = 0;
    this.urlKh = '';
    this.urlEn = '';
    this.sortOrder = 0;
    this.isEnabled = true;
    this.image = null;
    this.imagePreview = '';
    this.titleKh = '';
    this.titleEn = '';
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadLinks('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest();
    this.clearMsg();

    this.linksService.create(request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  update() {
    this.clearMsg();

    const request = this.getRequest();
    this.linksService.update(this.linkId, request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.linksService.delete(this.linkId).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }
}
